/**
 * Zwaailicht.nu - P2000 Alert Map
 * Main application entry point
 */

import { PUSH_BLOCKED_MESSAGE, STORAGE_KEYS } from './js/config.js';
import {
    userLocation,
    alerts,
    markers,
    activeFilters,
    currentClosestDistance,
    setUserLocation as setUserLocationState,
    setCurrentClosestDistance,
    setHasInitialFit,
    hasInitialFit,
    addLocatedAlert,
    addUnlocatedAlert,
    saveManualLocation,
    loadManualLocation,
    clearManualLocation,
    allDisplayedAlerts,
    currentAlertIndex,
    setCachedRouteDistance,
    filterRadiusKm,
} from './js/state.js';
import { getDistanceKm, loadValidCitySlugs } from './js/utils.js';
import {
    initMap,
    map,
    setUserMarker,
    removeUserMarker,
    createMarker,
    addMarkerToMap,
    removeMarkerFromMap,
    updateTrackingBeam,
    fitBoundsToAlert,
    setView,
    updateMarkerOpacities,
    updateBurstMarkers,
    updateOverlapBadges,
    showRoutePolyline,
    clearRoutePolyline,
} from './js/map.js';
import {
    initCarousel,
    renderCarousel,
    getFilteredAlerts,
    focusAlert,
    focusAlertByIndex,
    scrollToStart,
    closeMobileOverlays,
} from './js/carousel.js';
import {
    playNotificationSound,
    showAlertFlash,
    showError,
    showToast,
    showWarning,
} from './js/notifications.js';
import {
    getRoute,
    decodePolyline,
} from './js/routing.js';
import {
    isWebPushAvailable,
    getWebPushState,
    initWebPush,
    updatePushLocation,
    syncCitySubscriptions,
} from './js/web-push.js';


// =============================================================================
// Initialization
// =============================================================================

// Initialize map
initMap();

// Initialize carousel
initCarousel();

// Load valid city slugs for link validation
loadValidCitySlugs();

// Update UI function
function updateUI() {
    renderCarousel();
    updateMarkerOpacities();
    updateBurstMarkers();
    updateOverlapBadges();
}


// =============================================================================
// User Location
// =============================================================================

function setUserLocation(lat, lon, isManual = false) {
    setUserLocationState({ lat, lon });

    if (isManual) {
        saveManualLocation({ lat, lon });
    }

    setUserMarker(lat, lon);
    hideLocationHint();
    updateUI();
    updatePushLocation(lat, lon);

    // Update tracking beam if we have an active alert
    if (allDisplayedAlerts.length > 0) {
        updateTrackingBeam(allDisplayedAlerts[currentAlertIndex]);
    }

    // Fit to show user + closest alert
    if (!hasInitialFit && alerts.size > 0) {
        fitToClosest();
        setHasInitialFit(true);
    }
}

function showLocationHint() {
    let hint = document.getElementById('location-hint');
    if (!hint) {
        hint = document.createElement('div');
        hint.id = 'location-hint';
        hint.className = 'location-hint';
        hint.innerHTML = '📍 Klik op de kaart om je locatie in te stellen';
        document.body.appendChild(hint);
    }
    hint.classList.add('visible');
}

function hideLocationHint() {
    const hint = document.getElementById('location-hint');
    if (hint) hint.classList.remove('visible');
}

function requestGeolocation() {
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            localStorage.setItem(STORAGE_KEYS.geoShared, '1');
            setUserLocation(pos.coords.latitude, pos.coords.longitude, false);
            setView(pos.coords.latitude, pos.coords.longitude, 13);
        },
        (err) => {
            console.warn('[Geo] getCurrentPosition failed:', err.code, err.message);
            if (err.code === 1) {
                // PERMISSION_DENIED — browser or OS blocked geolocation
                console.warn('[Geo] Permission denied. Check browser site settings or Permissions-Policy header.');
                localStorage.removeItem(STORAGE_KEYS.geoShared);
            }
            showLocationHint();
        },
        { enableHighAccuracy: true, timeout: 10000 }
    );
}

function showLocationButton() {
    let btn = document.getElementById('geolocate-btn');
    if (btn) { btn.classList.remove('hidden'); return; }

    btn = document.createElement('button');
    btn.id = 'geolocate-btn';
    btn.className = 'geolocate-btn';
    btn.title = 'Gebruik mijn locatie';
    btn.setAttribute('aria-label', 'Gebruik mijn locatie');
    btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/></svg><span>Locatie gebruiken</span>`;
    btn.addEventListener('click', () => {
        btn.classList.add('hidden');
        requestGeolocation();
        // Log permission state for debugging
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then(p => {
                console.log('[Geo] Permission state at click:', p.state);
            }).catch(() => {});
        }
    });
    document.getElementById('map').insertAdjacentElement('afterend', btn);
}

async function initUserLocation() {
    // Check for saved manual location first
    const saved = loadManualLocation();
    if (saved) {
        setUserLocation(saved.lat, saved.lon, false);
        setView(saved.lat, saved.lon, 11);
        return;
    }


    // Web: locate straight away if the reader shared before, otherwise
    // check permission state before prompting
    const shared = localStorage.getItem(STORAGE_KEYS.geoShared) === '1';
    if ('geolocation' in navigator && navigator.permissions) {
        try {
            const perm = await navigator.permissions.query({ name: 'geolocation' });
            if (perm.state === 'granted' || (shared && perm.state !== 'denied')) {
                requestGeolocation();
            } else {
                // Show button instead of prompting immediately
                showLocationButton();
            }
        } catch {
            // permissions API not supported
            if (shared) requestGeolocation();
            else showLocationButton();
        }
    } else if ('geolocation' in navigator) {
        if (shared) requestGeolocation();
        else showLocationButton();
    } else {
        showLocationHint();
    }
}

// Click map to set location manually
map.on('click', (e) => {
    if (!userLocation) {
        setUserLocation(e.latlng.lat, e.latlng.lng, true);
        setView(e.latlng.lat, e.latlng.lng, 11);
    } else {
        // Close mobile overlays (expanded card / list mode) on map tap
        closeMobileOverlays();
    }
});

initUserLocation();


// =============================================================================
// Fit to Closest
// =============================================================================

function fitToClosest() {
    const filtered = getFilteredAlerts();
    if (filtered.length === 0 || !userLocation) return;

    const closest = filtered[0];
    fitBoundsToAlert(closest);
}


// =============================================================================
// Recenter Button
// =============================================================================

const recenterBtn = document.getElementById('recenter-btn');
let longPressTimer = null;

recenterBtn.addEventListener('click', () => {
    if (userLocation) {
        const filtered = getFilteredAlerts();
        if (filtered.length > 0) {
            fitToClosest();
        } else {
            setView(userLocation.lat, userLocation.lon, 13);
        }
    } else {
        initUserLocation();
    }
});

function startLongPress() {
    longPressTimer = setTimeout(() => {
        // Reset location
        clearManualLocation();
        removeUserMarker();
        setUserLocationState(null);
        showLocationHint();
        updateUI();
    }, 1000);
}

function cancelLongPress() {
    if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
    }
}

recenterBtn.addEventListener('mousedown', startLongPress);
recenterBtn.addEventListener('touchstart', startLongPress);
recenterBtn.addEventListener('mouseup', cancelLongPress);
recenterBtn.addEventListener('mouseleave', cancelLongPress);
recenterBtn.addEventListener('touchend', cancelLongPress);


// =============================================================================
// Route Distance (auto-fetch for focused alert)
// =============================================================================

let currentRouteRequestId = 0;
let routeServiceDown = false;

// Called when focused alert changes — fetch route automatically
window.onAlertFocusChanged = async function() {
    await fetchRouteForCurrentAlert();
};

async function fetchRouteForCurrentAlert() {
    const requestId = ++currentRouteRequestId;

    if (!userLocation) {
        clearRoutePolyline();
        return;
    }

    const alert = allDisplayedAlerts[currentAlertIndex];
    if (!alert?.latitude || !alert?.longitude) {
        clearRoutePolyline();
        return;
    }

    if (routeServiceDown) return;

    clearRoutePolyline();

    try {
        const route = await getRoute(
            userLocation.lat, userLocation.lon,
            alert.latitude, alert.longitude
        );

        if (requestId !== currentRouteRequestId) return;

        if (route?.geometry) {
            const coords = decodePolyline(route.geometry);
            showRoutePolyline(coords, alert);
            routeServiceDown = false;

            if (route.distance_km !== undefined) {
                setCachedRouteDistance(alert.id, route.distance_km, route.duration_min || 0);
                renderCarousel();
            }
        } else {
            routeServiceDown = true;
            updateTrackingBeam(alert);
        }
    } catch (err) {
        if (requestId !== currentRouteRequestId) return;
        console.error('Failed to fetch route:', err);
        routeServiceDown = true;
        clearRoutePolyline();
        updateTrackingBeam(alert);
    }
}


// =============================================================================
// Keyboard Navigation (left/right arrows always control carousel)
// =============================================================================

document.addEventListener('keydown', (e) => {
    // Up/Left arrow - previous alert
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = Math.max(0, currentAlertIndex - 1);
        if (newIndex !== currentAlertIndex) {
            focusAlertByIndex(newIndex);
        }
    }
    // Down/Right arrow - next alert
    else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = Math.min(allDisplayedAlerts.length - 1, currentAlertIndex + 1);
        if (newIndex !== currentAlertIndex && newIndex >= 0) {
            focusAlertByIndex(newIndex);
        }
    }
    // Home - go to first alert
    else if (e.key === 'Home') {
        e.preventDefault();
        if (allDisplayedAlerts.length > 0) {
            focusAlertByIndex(0);
        }
    }
    // End - go to last alert
    else if (e.key === 'End') {
        e.preventDefault();
        if (allDisplayedAlerts.length > 0) {
            focusAlertByIndex(allDisplayedAlerts.length - 1);
        }
    }
});


// =============================================================================
// Add Alert
// =============================================================================

// Flag to batch UI updates during bulk load
let isBulkLoading = false;

// Batching for WebSocket alerts: buffer incoming alerts, render once per batch
let wsBatchTimer = null;
let wsBatchQueue = [];
const WS_BATCH_DELAY = 200; // ms to wait before flushing

function flushAlertBatch() {
    wsBatchTimer = null;
    if (wsBatchQueue.length === 0) return;

    const batch = wsBatchQueue;
    wsBatchQueue = [];

    // Track closest new alert for notification
    let closestNewDistance = Infinity;
    let closestNewService = null;

    for (const alert of batch) {
        if (!alert.latitude || !alert.longitude) {
            addUnlocatedAlert(alert);
            continue;
        }
        if (alerts.has(alert.id)) continue;

        const deletedId = addLocatedAlert(alert);
        if (deletedId) removeMarkerFromMap(deletedId);

        const marker = createMarker(alert, focusAlert);
        addMarkerToMap(alert.id, marker, alert.service);

        // Check proximity for notification
        if (userLocation && activeFilters.has(alert.service)) {
            const dist = getDistanceKm(userLocation.lat, userLocation.lon, alert.latitude, alert.longitude);
            if (dist <= filterRadiusKm && dist < closestNewDistance) {
                closestNewDistance = dist;
                closestNewService = alert.service;
            }
        }
    }

    // Single notification for closest alert in batch
    if (closestNewService && closestNewDistance < currentClosestDistance) {
        setCurrentClosestDistance(closestNewDistance);
        playNotificationSound();
        showAlertFlash(closestNewService);
        fitToClosest();
        setTimeout(scrollToStart, 100);
    }

    // Single UI update for entire batch
    updateUI();

    if (!hasInitialFit && userLocation) {
        fitToClosest();
        setHasInitialFit(true);
    }
}

function addAlert(alert) {
    if (isBulkLoading) {
        // Bulk load: process immediately without UI updates (same as before)
        if (!alert.latitude || !alert.longitude) {
            addUnlocatedAlert(alert);
            return;
        }
        if (alerts.has(alert.id)) return;
        const deletedId = addLocatedAlert(alert);
        if (deletedId) removeMarkerFromMap(deletedId);
        const marker = createMarker(alert, focusAlert);
        addMarkerToMap(alert.id, marker, alert.service);
        return;
    }

    // WebSocket streaming: batch alerts and flush after delay
    wsBatchQueue.push(alert);
    if (!wsBatchTimer) {
        wsBatchTimer = setTimeout(flushAlertBatch, WS_BATCH_DELAY);
    }
}


// =============================================================================
// Navigation
// =============================================================================

window.navigate = function(lat, lon) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
        window.location.href = `maps://maps.apple.com/?daddr=${lat},${lon}&dirflg=d`;
    } else {
        const geoUri = `geo:${lat},${lon}?q=${lat},${lon}`;
        const link = document.createElement('a');
        link.href = geoUri;
        link.click();

        setTimeout(() => {
            if (userLocation) {
                window.open(`https://www.openstreetmap.org/directions?from=${userLocation.lat},${userLocation.lon}&to=${lat},${lon}`, '_blank');
            } else {
                window.open(`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`, '_blank');
            }
        }, 500);
    }
};

// Expose focusAlert globally for marker clicks
window.focusAlert = focusAlert;


// =============================================================================
// WebSocket Connection with Exponential Backoff
// =============================================================================

const WS_CONFIG = {
    initialDelay: 1000,      // Start with 1 second
    maxDelay: 30000,         // Max 30 seconds
    maxRetries: 10,          // Give up after 10 retries
    backoffMultiplier: 2,    // Double each time
};

let wsRetryCount = 0;
let wsRetryDelay = WS_CONFIG.initialDelay;
let wsInstance = null;
let wsReconnectTimer = null;
// WS is the only initial-load path. Track whether the `type: 'initial'` batch
// ever arrived (regardless of whether its alerts had coordinates) so a failed
// connection surfaces the error, and guard the error toast to fire once.
let receivedInitial = false;
let initialErrorShown = false;

let wasConnected = false;

// Track individual API statuses
const apiStatus = {
    rss: true,
    geo: true,
};

function updateConnectionStatus(connected) {
    const statusDot = document.getElementById('status-dot');
    if (statusDot) {
        if (connected) {
            statusDot.classList.remove('disconnected');
            statusDot.setAttribute('aria-label', 'Verbonden met server');
            updateApiStatus('rss', true);
        } else {
            statusDot.classList.add('disconnected');
            statusDot.setAttribute('aria-label', 'Verbinding verbroken, opnieuw proberen...');
            updateApiStatus('rss', false);

            // Show warning on disconnect (if was connected)
            if (wasConnected) {
                showWarning('Verbinding verbroken, opnieuw verbinden...');
            }
        }
    }
    wasConnected = connected;
}

/**
 * Update individual API status indicator
 * @param {string} api - 'rss' or 'geo'
 * @param {boolean} ok - true if working, false if error
 */
function updateApiStatus(api, ok) {
    apiStatus[api] = ok;

    const dot = document.getElementById(`status-${api}`);
    if (dot) {
        dot.classList.remove('ok', 'error');
        dot.classList.add(ok ? 'ok' : 'error');
    }

    // Update main status dot based on overall health
    const mainDot = document.getElementById('status-dot');
    if (mainDot) {
        const allOk = Object.values(apiStatus).every(Boolean);
        const someOk = Object.values(apiStatus).some(Boolean);

        mainDot.classList.remove('disconnected', 'degraded');
        if (!apiStatus.rss) {
            mainDot.classList.add('disconnected');
        } else if (!allOk && someOk) {
            mainDot.classList.add('degraded');
        }
    }
}

// Expose for use by other modules
window.updateApiStatus = updateApiStatus;

function connect() {
    // Cancel any pending reconnect timer
    if (wsReconnectTimer) {
        clearTimeout(wsReconnectTimer);
        wsReconnectTimer = null;
    }

    // Clean up existing connection
    if (wsInstance) {
        wsInstance.onclose = null; // Prevent reconnect on manual close
        wsInstance.close();
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsInstance = new WebSocket(`${protocol}//${location.host}/ws`);

    wsInstance.onopen = () => {
        console.log('WebSocket connected');
        updateConnectionStatus(true);
        // Reset retry state on successful connection
        wsRetryCount = 0;
        wsRetryDelay = WS_CONFIG.initialDelay;
    };

    wsInstance.onmessage = (e) => {
        try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'initial') {
                // Bulk-load fast path: single UI update after all alerts inserted.
                // This is the sole initial-load channel (the old /api/alerts
                // fetch was removed), so it also owns the fit-to-closest.
                if (Array.isArray(msg.data)) {
                    isBulkLoading = true;
                    for (const alert of msg.data) addAlert(alert);
                    isBulkLoading = false;
                    receivedInitial = true;
                    updateUI();
                    if (userLocation && !hasInitialFit) {
                        fitToClosest();
                        setHasInitialFit(true);
                    }
                }
            } else if (msg.type === 'alert') {
                addAlert(msg.data);
            } else if (msg.type === 'bursts_active') {
                // The backend sends this on connect AND after every
                // BurstDetectionJob cycle. handleBurstsUpdate is
                // exposed on window because bursts.js is dynamically
                // imported below — same call, no polling required.
                if (window.__handleBurstsUpdate) {
                    window.__handleBurstsUpdate(msg.data || []);
                }
            }
        } catch (err) {
            console.error('Failed to parse WebSocket message:', err);
        }
    };

    wsInstance.onclose = (event) => {
        console.log(`WebSocket closed: code=${event.code}`);
        updateConnectionStatus(false);

        // Closed before the initial batch ever arrived: surface the error now
        // (once) rather than leaving the user on a skeleton through the whole
        // retry budget. If a later retry succeeds, the rendered list replaces it.
        if (!receivedInitial && !initialErrorShown) {
            showInitialLoadError();
            initialErrorShown = true;
        }

        // Check if we should retry
        if (wsRetryCount >= WS_CONFIG.maxRetries) {
            console.error(`WebSocket: giving up after ${WS_CONFIG.maxRetries} retries`);
            return;
        }

        // Schedule reconnect with exponential backoff
        wsRetryCount++;
        console.log(`WebSocket: reconnecting in ${wsRetryDelay}ms (attempt ${wsRetryCount}/${WS_CONFIG.maxRetries})`);

        // Add jitter (±20%) to prevent thundering herd on server restart
        const jitteredDelay = wsRetryDelay * (0.8 + Math.random() * 0.4);
        wsReconnectTimer = setTimeout(() => {
            wsReconnectTimer = null;
            connect();
            // Increase delay for next attempt (with cap)
            wsRetryDelay = Math.min(wsRetryDelay * WS_CONFIG.backoffMultiplier, WS_CONFIG.maxDelay);
        }, jitteredDelay);
    };

    wsInstance.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Let onclose handle reconnection
    };
}

connect();


// =============================================================================
// Initial Data Load
// =============================================================================

// Show loading skeleton
const panelListEl = document.getElementById('alert-list');
if (panelListEl) {
    panelListEl.innerHTML = `
        <div class="skeleton-row" style="margin:4px 8px;border-radius:10px;background:rgba(0,0,0,0.02)"><div class="skeleton-dot"></div><div class="skeleton-body"><div class="skeleton-line" style="width:65%"></div><div class="skeleton-line" style="width:40%"></div></div></div>
        <div class="skeleton-row" style="margin:4px 8px;border-radius:10px;background:rgba(0,0,0,0.02)"><div class="skeleton-dot"></div><div class="skeleton-body"><div class="skeleton-line" style="width:55%"></div><div class="skeleton-line" style="width:35%"></div></div></div>
        <div class="skeleton-row" style="margin:4px 8px;border-radius:10px;background:rgba(0,0,0,0.02)"><div class="skeleton-dot"></div><div class="skeleton-body"><div class="skeleton-line" style="width:70%"></div><div class="skeleton-line" style="width:45%"></div></div></div>
    `;
}

// The initial alert batch arrives over the WebSocket as a `type: 'initial'`
// message (see connect() above). The skeleton stays visible until that lands;
// if the WS closes before it arrives, onclose calls this once to swap the
// skeleton for an error state.
function showInitialLoadError() {
    showError('Kon meldingen niet laden');
    if (panelListEl) {
        panelListEl.innerHTML = `
            <div class="panel-empty">
                <span class="empty-icon">😔</span>
                <p>Kon meldingen niet laden</p>
                <p class="empty-hint">Ververs de pagina om opnieuw te proberen</p>
            </div>
        `;
    }
}


// =============================================================================
// Service Worker (PWA)
// =============================================================================

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js')
        .then(() => {
            console.log('SW registered');
            // Initialize web push after SW is ready
            initWebPush().then(() => {
                updatePushBellUI();
                syncCitySubscriptions();
            });
        })
        .catch((err) => console.log('SW registration failed:', err));
}


// =============================================================================
// Web Push Notification Bell + Settings Overlay
// =============================================================================

function updatePushBellUI() {
    const bell = document.getElementById('push-bell');
    if (!bell) return;

    if (!isWebPushAvailable()) {
        bell.classList.add('hidden');
        return;
    }

    bell.classList.remove('hidden', 'active', 'denied', 'prompt');

    const state = getWebPushState();
    if (state === 'granted') {
        bell.classList.add('active');
        bell.title = 'Pushnotificaties instellingen';
    } else if (state === 'denied') {
        bell.classList.add('denied');
        bell.title = 'Pushnotificaties geblokkeerd in browser';
    } else {
        bell.classList.add('prompt');
        bell.title = 'Pushnotificaties aanzetten';
    }
}

const PUSH_SETTINGS_URL = '/volg#push-panel';

// The bell opens the notification settings as a sheet over the map — the same
// panel the header's "notificaties" opens on every other page
// (volg-settings.js), with enabling at the bottom of it. The map doesn't load
// that panel, its styles or seo-push.js (the API it drives), so the first tap
// loads the three; their URLs are on the bell. If that fails, /volg has it.
let pushSettingsLoading = null;

function loadPushSettings() {
    if (window.ZwaailichtSettings) return Promise.resolve();
    if (!pushSettingsLoading) {
        const d = pushBell.dataset;
        const load = (el) => new Promise((resolve, reject) => {
            el.onload = resolve;
            el.onerror = reject;
            document.head.appendChild(el);
        });
        const script = (src) => Object.assign(document.createElement('script'), { src });
        const css = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: d.cssSrc });
        pushSettingsLoading = Promise.all([load(css), window.ZwaailichtPush ? null : load(script(d.pushSrc))])
            .then(() => load(script(d.settingsSrc)))
            .catch((err) => { pushSettingsLoading = null; throw err; });
    }
    return pushSettingsLoading;
}

// The panel's confirmations use the site-wide toast name.
window.zwShowToast = (message, type) => showToast(message, type);

const pushBell = document.getElementById('push-bell');
if (pushBell) {
    pushBell.addEventListener('click', async () => {
        if (getWebPushState() === 'denied') {
            showWarning(PUSH_BLOCKED_MESSAGE);
            return;
        }
        try {
            await loadPushSettings();
            window.ZwaailichtSettings.openSheet(pushBell);
        } catch {
            window.location.href = PUSH_SETTINGS_URL;
        }
    });
}

// Push switched on or off in the panel: repaint the bell, and hand the new
// registration the location the map already has (it registers without one).
window.addEventListener('zw-push-change', () => {
    updatePushBellUI();
    if (!userLocation) return;
    updatePushLocation(userLocation.lat, userLocation.lon).then(ok => {
        if (ok) window.ZwaailichtSettings?.refresh();
    });
});

// Initial UI state (before SW is ready, hide if not available)
updatePushBellUI();


// =============================================================================
// Burst Detection
// =============================================================================

import { initBurstDetection } from './js/bursts.js';
import { initTrending } from './js/trending.js';
import { initLiveViewers } from './js/live-viewers.js';

// bursts.js sets ``window.__handleBurstsUpdate`` at module-eval time
// (see comment in that file) — by the time the WS handler at line
// ~690 looks it up, it's already there. We keep the 3 s setTimeout
// just for the DOM-touching ``initBurstDetection`` call so it
// happens after first paint.
setTimeout(() => {
    initBurstDetection();
    initTrending();
    initLiveViewers();
}, 3000);
